import { t } from "@lingui/macro";
import { ArrowRight, Crown, X } from "@phosphor-icons/react";
import { Button } from "@stylingcv/ui";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  currentCount: number;
  maxAllowed: number;
};

export const DocumentLimitModal = ({ isOpen, onClose, currentCount, maxAllowed }: Props) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="limit-modal-title"
            aria-describedby="limit-modal-desc"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative z-10 mx-4 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              aria-label={t`Close dialog`}
              className="absolute right-4 top-4 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
            >
              <X size={20} aria-hidden="true" />
            </button>

            {/* Content */}
            <div className="p-6 text-center">
              {/* Icon */}
              <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                <Crown size={32} className="text-amber-600 dark:text-amber-400" weight="fill" />
              </div>

              {/* Title */}
              <h2 id="limit-modal-title" className="mb-2 text-xl font-bold text-gray-900 dark:text-white">
                {t`Document Limit Reached`}
              </h2>

              {/* Description */}
              <p id="limit-modal-desc" className="mb-6 text-gray-600 dark:text-gray-400">
                {t`You've used all ${currentCount} of your ${maxAllowed} available documents. Upgrade your plan to create more resumes and cover letters.`}
              </p>

              {/* Stats */}
              <div className="mb-6 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {currentCount}/{maxAllowed}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t`Documents Used`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <Button asChild className="w-full gap-2 bg-amber-600 hover:bg-amber-700">
                  <Link to="/pricing">
                    <Crown size={16} weight="fill" />
                    {t`Upgrade Plan`}
                    <ArrowRight size={16} className="rtl:rotate-180" />
                  </Link>
                </Button>
                <Button variant="outline" onClick={onClose} className="w-full">
                  {t`Maybe Later`}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

